import { useState, useEffect } from "react";
import { Typography, Stack, LinearProgress, Box, Tooltip, Chip, Avatar } from "@mui/material";
import FileDataTable from "./FileDataTable";
import FileInput from "./FileInput";
import TemplateFormat from "./TemplateFormat";
import ColumnMapping from "./ColumnMapping";
import { useSearchParams } from "react-router-dom";
import { DefaultError } from "@tanstack/react-query";
import { ErrorHandler } from "../../list-manager/error-handling";
import { WarningAmber } from "@mui/icons-material";
import { useAllContacts } from "../../list-manager/api";

import * as XLSX from "xlsx";

type FileReaderProps = {
  onDataReady: (contacts: ContactToUpload[] | undefined) => void;
  error: DefaultError | undefined | null;
  isSuccess: boolean;
  isPending: boolean;
};

export default function FileReader({onDataReady, error, isSuccess, isPending,}: FileReaderProps) {
  const [searchParams] = useSearchParams();
  const list_id = searchParams.get("id");
  const [formattedData, setFormattedData] = useState<RowObject[]>();
  const [rawData, setRawData] = useState<string[][]>();
  const [hhColumn, setHHColumn] = useState<string | undefined>();
  const [agentColumn, setAgentColumn] = useState<string | undefined>();
  const [skippedDuplicates, setSkippedDuplicates] = useState<ContactToUpload[] | undefined>();
  const [skippedMissingHH, setSkippedMissingHH] = useState<ContactToUpload[] | undefined>();
  const [skippedExistingDuplicates, setSkippedExistingDuplicates] = useState<ContactToUpload[] | undefined>();
  const [filename, setFilename] = useState("");
  const [returnedData, setReturnedData] = useState<any>();
  const [columns, setColumns] = useState<string[]>([]);


  console.log("formattedData", formattedData)
  console.log("raw data", rawData)
  console.log("columns", columns)

  const { data: existingContacts, isLoading } = useAllContacts({list_id: list_id || ""});

  const handleFileUpload = async (file: File) => {
    setFilename(file.name);

    const fileExtension = file.name.split(".").pop()?.toLowerCase();

    const data = await file.arrayBuffer();
    let workbook;

    try {
      workbook = XLSX.read(data, {
        type: "array",
        raw: false,
        codepage: 65001,
      });
    } catch (err) {
      console.error("Error parsing file:", err);
      return;
    }

    const sheetName = workbook.SheetNames[0];
    const worksheet = workbook.Sheets[sheetName];

    let jsonData: RowObject[] = [];
    let parsedData: string[][] = [];

    if (fileExtension === "csv") {
      // CSV parsing: XLSX handles this natively
      const csvText = await file.text();
      const result = XLSX.read(csvText, { type: "string" });
      const sheet = result.Sheets[result.SheetNames[0]];
      jsonData = XLSX.utils.sheet_to_json(sheet, { defval: "" });
    } else {
      // XLSX / XLS parsing
      jsonData = XLSX.utils.sheet_to_json(worksheet, { defval: "" });
    }

    // Convert to string[][]
    parsedData = [Object.keys(jsonData[0] || {})];
    jsonData.forEach((row) => {
      parsedData.push(Object.values(row).map(String));
    });

    console.log("json data", jsonData);

    setFormattedData(
      jsonData.map((row, rowIndex) => ({ mui_row_id: rowIndex, ...row })),
    );
    setColumns(Object.keys(jsonData[0]).filter((v) => isNaN(parseInt(v))));
    setRawData(parsedData);
  };

  useEffect(() => {
    if (hhColumn && agentColumn && formattedData?.length) {
      const listName = searchParams.get("name");
      const existingHHSet = new Set(
        existingContacts?.map((c: Contact) => c.household_number?.toString()) ?? [],
      );

      const seen = new Set<string>();
      const duplicates: any[] = [];
      const existingDupes: any[] = [];
      const missingHH: any[] = [];

      const validContacts: ContactToUpload[] = [];

      formattedData.forEach((row) => {
        const hhRaw = row[hhColumn];
        const agent = row[agentColumn];

        if (!hhRaw) {
          missingHH.push(row);
          return;
        }

        const hh = hhRaw.toString();

        if (seen.has(hh)) {
          duplicates.push(row);
          return;
        }

        if (existingHHSet.has(hh)) {
          existingDupes.push(row);
          return;
        }

        if (hh && agent) {
          seen.add(hh);
          validContacts.push({
            household_id: hh,
            agent_email: agent,
            list_name: listName,
          });
        }
      });

      setSkippedDuplicates(duplicates);
      setSkippedExistingDuplicates(existingDupes);
      setSkippedMissingHH(missingHH);
      onDataReady(validContacts);
    } else {
      onDataReady(undefined);
    }
  }, [hhColumn, agentColumn, formattedData]);

  console.log("skipped duplicates", skippedDuplicates)


  const onHHSelect = (hh_id: string) => {
    setHHColumn(hh_id);
    if(hh_id === agentColumn) {
      setAgentColumn(undefined);
    }
  };

  const onAgentSelect = (agent_email: string) => {
    setAgentColumn(agent_email);
    if (agent_email === hhColumn) {
      setHHColumn(undefined);
    }
  };

  const exportSelection = (rowsToExport: any[], fileName: string) => {
    const cleanedRows = rowsToExport.map(({ mui_row_id, ...rest }) => rest);
    const worksheet = XLSX.utils.json_to_sheet(cleanedRows);

    // Set custom column widths (example: 20 characters wide for each column)
    const numCols = Object.keys(rowsToExport[0] || {}).length;
    worksheet["!cols"] = Array(numCols).fill({ wch: 20 });

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, fileName);

    XLSX.writeFile(workbook, `${fileName}.xlsx`);
  };

  if (isPending) {
    return (
      <Box sx={{ width: "30vw", padding: 2 }}>
        <LinearProgress />
      </Box>
    );
  }

  if (isSuccess) {
    return (
      <Box sx={{ width: "30vw", padding: 2 }}>
        <Typography
          style={{ fontSize: 14, fontWeight: "bold", textAlign: "center" }}
        >
          Contacts Uploaded Successfully!
        </Typography>
      </Box>
    );
  }

  return (
    <div
      style={{
        padding: 3,
        // minHeight: "calc(100vh - 120px)",
        // maxHeight: "calc(100vh - 120px)",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Stack gap={2}>
        <Stack gap={2}>
          <FileInput handleFileSelect={handleFileUpload} />
          <TemplateFormat />
        </Stack>
        {formattedData && (
          <Stack gap={2}>
            <ErrorHandler error={error} />
            <ColumnMapping
              columns={columns}
              selectedAgent={agentColumn}
              selectedHH={hhColumn}
              onAgentSelect={onAgentSelect}
              onHHSelect={onHHSelect}
            />
            <Stack direction="row" spacing={1}>
              {!!skippedDuplicates?.length && (
                <Stack alignItems="center">
                <Tooltip
                  title={
                    <Box>
                      <div>
                        {skippedDuplicates.length} Duplicate Household ID(s)
                        will not be Uploaded:
                      </div>
                      <ul style={{ margin: 0, paddingLeft: 16 }}>
                        {skippedDuplicates.slice(0, 10).map((row, idx) => (
                          <li key={idx}>
                            {(row as Record<string, any>)[
                              hhColumn!
                            ]?.toString() || "(blank)"}
                          </li>
                        ))}
                        {skippedDuplicates.length > 5 && (
                          <li>...and {skippedDuplicates.length - 10} more</li>
                        )}
                      </ul>
                    </Box>
                  }
                  placement="top"
                  arrow
                >
                  <Chip
                    icon={<WarningAmber style={{ fontSize: 20 }} />}
                    label={`${skippedDuplicates.length} Duplicate(s) Skipped`}
                    color="warning"
                    variant="outlined"
                    style={{ paddingLeft: 5 }}
                  />
                </Tooltip>
                <Typography
                    variant="body2"
                    sx={{
                      display: "inline-block",
                      color: "primary.main",
                      cursor: "pointer",
                      textDecoration: "underline",
                      ml: 1,
                    }}
                    onClick={() =>
                      exportSelection(
                        skippedDuplicates,
                        "duplicates",
                      )
                    }
                  >
                    Export
                  </Typography>
                </Stack>
              )}
              {!!skippedMissingHH?.length && (
                <Stack alignItems="center">
                  <Tooltip
                    title={`${skippedMissingHH.length} record(s) missing household ID skipped`}
                    placement="top"
                    arrow
                  >
                    <Chip
                      icon={<WarningAmber style={{ fontSize: 20 }} />}
                      label={`${skippedMissingHH.length} Missing HH Skipped`}
                      color="warning"
                      variant="outlined"
                      style={{ paddingLeft: 5 }}
                    />
                  </Tooltip>
                  <Typography
                    variant="body2"
                    sx={{
                      display: "inline-block",
                      color: "primary.main",
                      cursor: "pointer",
                      textDecoration: "underline",
                      ml: 1,
                    }}
                    onClick={() =>
                      exportSelection(
                        skippedMissingHH,
                        "missing-hh",
                      )
                    }
                  >
                    Export
                  </Typography>
                </Stack>
              )}
              {!!skippedExistingDuplicates?.length && (
                <Stack alignItems="center">
                  <Tooltip
                    title={
                      <Box>
                        <div>
                          {skippedExistingDuplicates.length} Household ID(s)
                          already exist in this list:
                        </div>
                        <ul style={{ margin: 0, paddingLeft: 16 }}>
                          {skippedExistingDuplicates
                            .slice(0, 10)
                            .map((row, idx) => (
                              <li key={idx}>
                                {(row as Record<string, any>)[
                                  hhColumn!
                                ]?.toString() || "(blank)"}
                              </li>
                            ))}
                          {skippedExistingDuplicates.length > 10 && (
                            <li>
                              ...and {skippedExistingDuplicates.length - 10}{" "}
                              more
                            </li>
                          )}
                        </ul>
                      </Box>
                    }
                    placement="top"
                    arrow
                  >
                    <Chip
                      icon={<WarningAmber style={{ fontSize: 20 }} />}
                      label={`${skippedExistingDuplicates.length} Already Exist`}
                      color="warning"
                      variant="outlined"
                      style={{ paddingLeft: 5, marginRight: 8 }}
                    />
                  </Tooltip>
                  <Typography
                    variant="body2"
                    sx={{
                      display: "inline-block",
                      color: "primary.main",
                      cursor: "pointer",
                      textDecoration: "underline",
                      ml: 1,
                    }}
                    onClick={() =>
                      exportSelection(
                        skippedExistingDuplicates,
                        "existing-duplicates",
                      )
                    }
                  >
                    Export
                  </Typography>
                </Stack>
              )}
            </Stack>
            <FileDataTable
              selectedAgentColumn={agentColumn}
              selectedHHColumn={hhColumn}
              rows={formattedData}
            />
          </Stack>
        )}
      </Stack>
    </div>
  );
}
